<script setup lang="ts">
import { computed, nextTick, ref, useId, watch } from 'vue';

import type { AnalyticsFilters, AnalyticsPeriodPreset } from '@/shared/lib';

import { buildAnalyticsPreset, COMPARISON_OPTIONS, getAnalyticsDateRangeError } from '@/shared/lib';
import { AppButton, BaseDropdown, IconCalendar, IconChevronDown } from '@/shared/ui';
import { todayInTimezone } from '@shared/lib/date-time';

import DateRangeFields from './DateRangeFields.vue';

interface Props {
  filters: AnalyticsFilters;
  timezone: string;
  /** Показывать выбор базы сравнения (только для страниц аналитики). */
  comparison?: boolean;
  quickPresets?: ReadonlyArray<{ id: Exclude<AnalyticsPeriodPreset, 'custom'>; label: string }>;
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  quickPresets: () => [
    { id: 'today', label: 'Сегодня' },
    { id: 'last-7-days', label: '7д' },
  ],
  label: 'Период аналитики',
  comparison: false,
});
const emit = defineEmits<{
  change: [filters: AnalyticsFilters];
}>();

const today = computed(() => todayInTimezone(props.timezone));
const draftDateFrom = ref(props.filters.dateFrom);
const draftDateTo = ref(props.filters.dateTo);
const triggerRef = ref<HTMLButtonElement | null>(null);
const dropdownContentRef = ref<HTMLFormElement | null>(null);
const popoverId = `analytics-period-${useId()}`;
const errorId = `${popoverId}-error`;

/** Общий каталог; страница может вынести подходящие ей периоды в быстрые кнопки. */
const allPresets = [
  { id: 'today', label: 'Сегодня' },
  { id: 'last-7-days', label: '7д' },
  { id: 'last-30-days', label: '30 дней' },
  { id: 'last-3-months', label: '3 месяца' },
  { id: 'last-6-months', label: '6 месяцев' },
  { id: 'last-12-months', label: '12 месяцев' },
  { id: 'current-month', label: 'Текущий месяц' },
  { id: 'previous-month', label: 'Прошлый месяц' },
  { id: 'year-to-date', label: 'С начала года' },
] as const satisfies ReadonlyArray<{ id: Exclude<AnalyticsPeriodPreset, 'custom'>; label: string }>;

const dropdownPresets = computed(() =>
  allPresets.filter((preset) => !props.quickPresets.some((quick) => quick.id === preset.id)),
);
const activePreset = computed(() => props.filters.preset);
/** Лейбл триггера выводится из `dropdownPresets`, чтобы не расходиться со списком. */
const dropdownPresetLabel = computed(
  () =>
    dropdownPresets.value.find((preset) => preset.id === activePreset.value)?.label ??
    (activePreset.value === 'custom' ? 'Произвольный период' : null),
);
const isDropdownPresetActive = computed(() => dropdownPresetLabel.value !== null);
const dropdownTriggerLabel = computed(() => dropdownPresetLabel.value ?? 'Период');
const draftError = computed(() => getAnalyticsDateRangeError(draftDateFrom.value, draftDateTo.value, today.value));

/** Общий стиль пунктов выпадающего списка: готовые периоды и база сравнения. */
const optionClass =
  'flex min-h-11 w-full cursor-pointer items-center rounded-lg px-3 text-left text-sm font-medium transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-(--accent) focus-visible:outline-none';
const activeOptionClass = 'bg-(--accent) text-(--on-accent)';
const idleOptionClass = 'text-(--text-primary) hover:bg-(--surface-hover)';

watch(
  () => [props.filters.dateFrom, props.filters.dateTo] as const,
  ([dateFrom, dateTo]) => {
    draftDateFrom.value = dateFrom;
    draftDateTo.value = dateTo;
  },
);

const applyPreset = (preset: Exclude<AnalyticsPeriodPreset, 'custom'>): void => {
  emit('change', buildAnalyticsPreset(preset, props.filters, today.value));
};

const focusTrigger = (): void => {
  void nextTick(() => triggerRef.value?.focus());
};

const closeDropdown = (close: () => void): void => {
  close();
  focusTrigger();
};

const focusDropdownStart = (): void => {
  void nextTick(() => dropdownContentRef.value?.querySelector<HTMLButtonElement>('[data-period-preset]')?.focus());
};

const toggleDropdown = (toggle: () => void, isOpen: boolean): void => {
  toggle();

  if (!isOpen) {
    focusDropdownStart();
  }
};

const applyDropdownPreset = (preset: Exclude<AnalyticsPeriodPreset, 'custom'>, close: () => void): void => {
  applyPreset(preset);
  closeDropdown(close);
};

const applyComparison = (compare: AnalyticsFilters['compare']): void => {
  emit('change', { ...props.filters, compare });
};

const applyCustom = (close: () => void): void => {
  if (draftError.value) {
    return;
  }

  emit('change', {
    ...props.filters,
    dateFrom: draftDateFrom.value,
    dateTo: draftDateTo.value,
    preset: 'custom',
  });
  closeDropdown(close);
};
</script>

<template>
  <div
    class="flex max-w-full items-center gap-1 overflow-x-auto rounded-xl bg-(--surface-sunken) p-1"
    :aria-label="label"
  >
    <button
      v-for="preset in quickPresets"
      :key="preset.id"
      type="button"
      class="min-h-11 shrink-0 cursor-pointer rounded-lg px-2.5 text-xs font-medium transition-[background-color,color,transform,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-(--accent) focus-visible:outline-none active:scale-[0.97] sm:text-sm"
      :class="
        activePreset === preset.id
          ? 'bg-(--accent) text-(--on-accent) shadow-sm'
          : 'text-(--text-secondary) hover:bg-(--surface-hover) hover:text-(--text-primary)'
      "
      :aria-pressed="activePreset === preset.id"
      @click="applyPreset(preset.id)"
    >
      {{ preset.label }}
    </button>

    <BaseDropdown
      class="shrink-0"
      width="min(360px, calc(100vw - 2rem))"
      :content="{ align: 'end', side: 'bottom', sideOffset: 10 }"
    >
      <template #trigger="{ isOpen, toggle }">
        <button
          ref="triggerRef"
          type="button"
          class="flex min-h-11 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium transition-[background-color,color,transform,box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-(--accent) focus-visible:outline-none active:scale-[0.97] sm:text-sm"
          :class="
            isDropdownPresetActive
              ? 'bg-(--accent) text-(--on-accent) shadow-sm'
              : 'text-(--text-secondary) hover:bg-(--surface-hover) hover:text-(--text-primary)'
          "
          aria-haspopup="dialog"
          :aria-expanded="isOpen"
          :aria-controls="popoverId"
          @click="toggleDropdown(toggle, isOpen)"
        >
          <IconCalendar
            aria-hidden="true"
            :width="14"
            :height="14"
          />
          {{ dropdownTriggerLabel }}
          <IconChevronDown
            aria-hidden="true"
            class="transition-transform duration-150"
            :class="{ 'rotate-180': isOpen }"
            :width="9"
            :height="9"
          />
        </button>
      </template>

      <template #content="{ close }">
        <form
          :id="popoverId"
          ref="dropdownContentRef"
          role="dialog"
          :aria-label="label"
          class="flex flex-col p-4"
          @submit.prevent="applyCustom(close)"
          @keydown.esc.prevent="closeDropdown(close)"
        >
          <div
            role="group"
            class="flex flex-col gap-1"
            aria-label="Готовые периоды"
          >
            <button
              v-for="preset in dropdownPresets"
              :key="preset.id"
              type="button"
              data-period-preset
              :class="[optionClass, activePreset === preset.id ? activeOptionClass : idleOptionClass]"
              :aria-pressed="activePreset === preset.id"
              @click="applyDropdownPreset(preset.id, close)"
            >
              {{ preset.label }}
            </button>
          </div>

          <div class="my-3 h-px bg-(--border)" />

          <div class="flex flex-col gap-4">
            <div>
              <p class="text-sm font-semibold text-(--text-primary)">Произвольный период</p>
              <p class="mt-1 text-xs text-(--text-secondary)">До 366 календарных дней включительно.</p>
            </div>

            <DateRangeFields
              v-model:date-from="draftDateFrom"
              v-model:date-to="draftDateTo"
              :today="today"
              :error="draftError"
              :error-id="errorId"
            />

            <p
              v-if="draftError"
              :id="errorId"
              role="alert"
              class="text-sm text-(--danger)"
            >
              {{ draftError }}
            </p>

            <div class="flex justify-end">
              <AppButton
                type="submit"
                :disabled="Boolean(draftError)"
              >
                Применить
              </AppButton>
            </div>
          </div>

          <template v-if="comparison">
            <div class="my-3 h-px bg-(--border)" />

            <div>
              <p class="text-sm font-semibold text-(--text-primary)">База сравнения</p>
              <p class="mt-1 text-xs text-(--text-secondary)">С чем сопоставлять KPI и ряды выбранного периода.</p>
            </div>

            <div
              role="group"
              class="mt-3 flex flex-col gap-1"
              aria-label="База сравнения"
            >
              <button
                v-for="option in COMPARISON_OPTIONS"
                :key="option.label"
                type="button"
                :class="[optionClass, filters.compare === option.compare ? activeOptionClass : idleOptionClass]"
                :aria-pressed="filters.compare === option.compare"
                @click="applyComparison(option.compare)"
              >
                {{ option.label }}
              </button>
            </div>
          </template>
        </form>
      </template>
    </BaseDropdown>
  </div>
</template>
